import React, {
  useContext,
  useRef,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import {
  ACTION_RESET_QUERY,
  searchContextStore,
} from '../search/SearchContextProvider';
import useDebounce from '@fec/frontend/foundation/hooks/Debounce';
import Typeahead, {
  defaultMatcher,
} from '@fec/frontend/modules/inputs/typeahead/typeahead';
import { translate } from '../searchHelpers';
import { TRACK_INTERACTION, triggerEvent } from '@fec/assets/js/utils/event';
import SvgIcon from '@fec/frontend/elements/icons/svg-icon/svg-icon';

const TYPEAHEAD_MAX_RESULTS = 5;

// eslint-disable-next-line react/display-name
const SearchInput = forwardRef((props, ref) => {
  const { state: searchContext, dispatch } = useContext(searchContextStore);
  // Only used when Typeahead is enabled (is not a state var since it does not affect rendering here)
  let autocompleteSize = 0;

  const _t = translate.bind(
    null,
    searchContext.searchConfig,
    searchContext.translations,
  );

  const inputRef = useRef(null);

  const resetInput = () => {
    props.onchange('');
    if (inputRef.current) {
      inputRef.current.focus();
    }

    dispatch({ type: ACTION_RESET_QUERY });
  };

  const autocompleteEndpoint =
    searchContext.searchConfig.name === 'audio' &&
    searchContext.searchConfig.audioSearch.autocompleteEndpoint
      ? searchContext.searchConfig.audioSearch.autocompleteEndpoint.replace(
          'BUSINESSUNIT',
          searchContext.searchConfig.businessUnit,
        )
      : false;

  const placeholderKey =
    searchContext.searchConfig.name === 'audio' &&
    searchContext.searchConfig.audioSearch.placeholderKey
      ? searchContext.searchConfig.audioSearch.placeholderKey
      : 'searchPlaceholder';

  const [suggestions, setSuggestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 250);

  useEffect(() => {
    if (debouncedSearchTerm.length < 2) {
      return;
    }

    fetch(
      `${autocompleteEndpoint}?q=${debouncedSearchTerm}&pageSize=8${searchContext.searchConfig.name === 'audio' ? '&mediaType=AUDIO' : ''}`,
    )
      .then((result) => result.json())
      .then((data) => {
        setSuggestions(
          data.searchResultShowList.map((show) => {
            return {
              text: show.title,
              id: show.urn,
              url: `/redirect/byshowid/${show.urn}`,
              matchIndex: 0,
            };
          }),
        );
      });
  }, [debouncedSearchTerm]);

  const handleChange = (e) => {
    props.onchange(e.target.value);
  };

  const handleTypeaheadChange = (event, { newValue, method }) => {
    if (method !== 'down' && method !== 'up') {
      setSearchTerm(newValue);
    }
    props.onchange(newValue);
  };

  const handleTypeaheadSelection = (
    event,
    { suggestion, suggestionValue, suggestionIndex },
  ) => {
    if (suggestion.url && suggestion.url.length > 0) {
      triggerEvent(TRACK_INTERACTION, {
        event_source: 'search_' + searchContext.searchConfig.name,
        event_name: suggestionValue + '|' + props.searchQuery,
        event_value: autocompleteSize + '-' + (suggestionIndex + 1),
        event_type: 'hidden_event',
      });
      window.location.href = suggestion.url;
    }
  };

  if (autocompleteEndpoint) {
    const customMatcher = () => {
      const results = defaultMatcher(debouncedSearchTerm, suggestions, 2);

      // Used for tracking, we need to know how many results were shown
      if (results && results.length !== autocompleteSize) {
        autocompleteSize =
          results.length > TYPEAHEAD_MAX_RESULTS
            ? TYPEAHEAD_MAX_RESULTS
            : results.length;

        if (props.trackAutocompleteSize) {
          props.trackAutocompleteSize(autocompleteSize);
        }
      }
      return results;
    };
    return (
      <div
        className="typeahead__wrapper"
        onFocus={props.onFocus}
        onBlur={props.onBlur}>
        <Typeahead
          suggestions={suggestions}
          placeholder={_t(placeholderKey).replace(
            // quick and dirty: JMSTranslate has problems with '&', so this will be replaced here
            '%brandName%',
            searchContext.searchConfig.businessUnit === 'rtr'
              ? searchContext.searchConfig.audioSearch.rtrBrandName
              : searchContext.searchConfig.audioSearch.srfBrandName,
          )}
          onChange={handleTypeaheadChange}
          value={props.searchQuery}
          onSuggestionSelected={handleTypeaheadSelection}
          onMatchRequest={customMatcher}
          onResetRequest={resetInput}
          maxSuggestions={TYPEAHEAD_MAX_RESULTS}
          translations={searchContext.translations}
        />
      </div>
    );
  }

  return (
    <div
      className="search-wrapper searchbox"
      ref={ref}
      onFocus={props.onFocus}
      onBlur={props.onBlur}>
      <div className="search__input--wrapper">
        <div className="search__icon"></div>
        <label className="search__label">
          <input
            ref={inputRef}
            className="search__input search__input--loaded search__input--rubric-search"
            name="q"
            type="text"
            autoComplete="off"
            maxLength={128}
            placeholder={_t(placeholderKey).replace(
              // quick and dirty: JMSTranslate has problems with '&', so this will be replaced here
              '%brandName%',
              searchContext.searchConfig.audioSearch.srfBrandName,
            )}
            value={props.searchQuery}
            onChange={handleChange}
          />
        </label>
        <a
          href="#"
          className="search__close js-search-close"
          title={_t('closeSearchInput')}
          onClick={() => resetInput()}
          aria-hidden="true"
          tabIndex="-1"
          style={
            props.searchQuery && props.searchQuery !== ''
              ? { display: 'inline' }
              : { display: 'none' }
          }>
          <SvgIcon styleModifier="svg-icon--close svg-icon--clear-input" />
        </a>
      </div>
    </div>
  );
});

export default SearchInput;
